import { supabase } from "@/integrations/supabase/client";
import {
  MenuCategory,
  MenuCategoryCreate,
  MenuCategoryUpdate,
} from "@/types/menu";
import { logActivityEvent } from "@/services/activity/activityLogService";
import { filterValidCategoryIds } from "@/utils/categoryUtils";

interface DatabaseCategory {
  id: string;
  store_id: string;
  name: string;
  description: string;
  image_url: string;
  position: number;
  created_at: string;
  updated_at: string;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  duration_minutes: number;
  available: boolean;
  max_advance_booking_days: number;
  store_id: string;
  created_at: string;
  updated_at: string;
  entity_type: string | null;
}

interface CategoryWithMenuItems extends MenuCategory {
  menu_items: MenuItem[];
}

export const fetchCategories = async (
  storeId: string
): Promise<MenuCategory[]> => {
  const { data, error } = await supabase
    .from("menu_categories")
    .select("*")
    .eq("store_id", storeId);
  if (error) {
    console.error("Error fetching categories......:", error);
    // throw error;
  }

  return data;
};

export const fetchMenuCategories = async (
  storeId: string
): Promise<CategoryWithMenuItems[]> => {
  try {
    const { data: menuItemsCategories, error: menuItemsCategoriesError } =
      await supabase
        .from("menu_items_categories")
        .select(
          `
          *,
          menu_categories!inner(*),
          menu_items!inner(*)
        `
        )
        .eq("menu_categories.store_id", storeId);

    if (menuItemsCategoriesError) {
      console.error(
        "Error fetching menu items categories:",
        menuItemsCategoriesError
      );
      throw menuItemsCategoriesError;
    }

    const categoriesMap = new Map<string, CategoryWithMenuItems>();

    menuItemsCategories?.forEach((item) => {
      const category = item.menu_categories as DatabaseCategory;
      if (!categoriesMap.has(category.id)) {
        categoriesMap.set(category.id, {
          id: category.id,
          store_id: category.store_id,
          name: category.name,
          description: category.description,
          imageUrl: category.image_url,
          image_url: category.image_url,
          position: category.position,
          created_at: category.created_at,
          updated_at: category.updated_at,
          menu_items: [],
        });
      }

      const existingCategory = categoriesMap.get(category.id);
      if (existingCategory && item.menu_items) {
        const menuItem = item.menu_items as MenuItem;
        existingCategory.menu_items.push(menuItem);
      }
    });

    return Array.from(categoriesMap.values());
  } catch (error) {
    console.error("Error in fetchCategories:", error);
    throw error;
  }
};

export const createCategory = async (
  data: MenuCategoryCreate
): Promise<MenuCategory> => {
  try {
    const { data: createdCategory, error } = (await supabase
      .from("menu_categories")
      .insert({
        store_id: data.store_id,
        name: data.name,
        description: data.description,
        image_url: data.imageUrl,
        position: data.position,
      })
      .select()
      .single()) as { data: DatabaseCategory; error: Error };

    if (error) {
      console.error("Error creating category:", error);
      throw error;
    }

    if (!createdCategory) {
      throw new Error("Failed to create category");
    }

    await logActivityEvent({
      entity_type: "menu_item",
      entity_id: createdCategory.id,
      user_name: "Admin",
      action: "category_created",
      details: {
        name: createdCategory.name,
        description: createdCategory.description,
      },
      store_id: createdCategory.store_id,
    });

    return {
      id: createdCategory.id,
      store_id: createdCategory.store_id,
      name: createdCategory.name,
      description: createdCategory.description,
      imageUrl: createdCategory.image_url,
      image_url: createdCategory.image_url,
      position: createdCategory.position,
      created_at: createdCategory.created_at,
      updated_at: createdCategory.updated_at,
    };
  } catch (error) {
    console.error("Error in createCategory:", error);
    throw error;
  }
};

export const updateCategory = async (
  id: string,
  data: MenuCategoryUpdate
): Promise<MenuCategory> => {
  try {
    // Fetch the existing category data
    const { data: existingCategory, error: fetchError } = (await supabase
      .from("menu_categories")
      .select("*")
      .eq("id", id)
      .single()) as { data: DatabaseCategory; error: Error };

    if (fetchError) {
      console.error("Error fetching existing category:", fetchError);
      throw fetchError;
    }

    const { data: updatedCategory, error: updateError } = (await supabase
      .from("menu_categories")
      .update({
        name: data.name !== undefined ? data.name : existingCategory.name,
        description:
          data.description !== undefined
            ? data.description
            : existingCategory.description,
        image_url:
          data.imageUrl !== undefined
            ? data.imageUrl
            : existingCategory.image_url,
        position:
          data.position !== undefined
            ? data.position
            : existingCategory.position,
      })
      .eq("id", id)
      .select()
      .single()) as { data: DatabaseCategory; error: Error };

    if (updateError) {
      console.error("Error updating category:", updateError);
      throw updateError;
    }

    if (!updatedCategory) {
      throw new Error("Failed to update category");
    }

    // Identify changes for activity logging
      const changes: Record<string, [string, string]> = {};
    if (data.name !== undefined && data.name !== existingCategory.name) {
      changes.name = [existingCategory.name, data.name];
    }
    if (
      data.description !== undefined &&
      data.description !== existingCategory.description
    ) {
      changes.description = [existingCategory.description, data.description];
    }
    if (
      data.imageUrl !== undefined &&
      data.imageUrl !== existingCategory.image_url
    ) {
      changes.imageUrl = [existingCategory.image_url, data.imageUrl];
    }
    if (
      data.position !== undefined &&
      data.position !== existingCategory.position
    ) {
      changes.position = [existingCategory.position.toString(), data.position.toString()];
    }

    await logActivityEvent({
      entity_type: "menu_item",
      entity_id: id,
      user_name: "Admin",
      action: "category_updated",
      details: changes,
      store_id: data.store_id || updatedCategory.store_id,
    });

    return {
      id: updatedCategory.id,
      store_id: updatedCategory.store_id,
      name: updatedCategory.name,
      description: updatedCategory.description,
      imageUrl: updatedCategory.image_url,
      image_url: updatedCategory.image_url,
      position: updatedCategory.position,
      created_at: updatedCategory.created_at,
      updated_at: updatedCategory.updated_at,
    };
  } catch (error) {
    console.error("Error in updateCategory:", error);
    throw error;
  }
};

export const deleteCategory = async (
  id: string,
  storeId: string
): Promise<boolean> => {
  try {
    // Fetch the category data before deleting
    const { data: categoryData, error: fetchError } = await supabase
      .from("menu_categories")
      .select("*")
      .eq("id", id)
      .single();

    if (fetchError) {
      console.error("Error fetching category before deleting:", fetchError);
      throw fetchError;
    }

    const { error } = await supabase
      .from("menu_categories")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting category:", error);
      throw error;
    }

    await logActivityEvent({
      entity_type: "menu_item",
      entity_id: id,
      user_name: "Admin",
      action: "category_deleted",
      details: { name: categoryData.name },
      store_id: storeId,
    });

    return true;
  } catch (error) {
    console.error("Error in deleteCategory:", error);
    throw error;
  }
};

export const updateItemCategories = async (
  itemId: string,
  categoryIds: string[]
): Promise<void> => {
  try {
    // First, get the menu item to get the store_id
    const { data: menuItem, error: fetchError } = await supabase
      .from("menu_items")
      .select("store_id")
      .eq("id", itemId)
      .single();

    if (fetchError) {
      console.error("Error fetching menu item:", fetchError);
      throw fetchError;
    }

    if (!menuItem) {
      throw new Error("Menu item not found");
    }

    // Remove existing categories for the item
    const { error: deleteError } = await supabase
      .from("menu_items_categories")
      .delete()
      .eq("menu_item_id", itemId);

    if (deleteError) {
      console.error("Error deleting existing item categories:", deleteError);
      throw deleteError;
    }

    // If there are no category IDs, just return
    if (!categoryIds || categoryIds.length === 0) {
      return;
    }

    // Filter out "all" and any other non-UUID values
    const validCategoryIds = filterValidCategoryIds(categoryIds);

    // If no valid category IDs remain, just return
    if (validCategoryIds.length === 0) {
      return;
    }

    // Insert new categories for the item with store_id
    const newCategories = validCategoryIds.map((categoryId) => ({
      menu_item_id: itemId,
      category_id: categoryId,
      store_id: menuItem.store_id,
    }));

    const { error: insertError } = await supabase
      .from("menu_items_categories")
      .insert(newCategories);

    if (insertError) {
      console.error("Error inserting new item categories:", insertError);
      throw insertError;
    }
  } catch (error) {
    console.error("Error in updateItemCategories:", error);
    throw error;
  }
};
